import { schema as initializeSchema } from '@unifyapps/defs/types/event/schema/onOzonetelInitialize';
import type { PageEventType } from '@unifyapps/defs/types/page';
import type { JSONSchema7 } from 'json-schema';
import { OZT_METHOD_TYPES } from './methods';
import { OZONE_TEL_AGENT_MODES, OZONE_TEL_AGENT_STATUSES } from './sdk';

const STRING_SCHEMA = { type: 'string' } satisfies JSONSchema7;
const BOOLEAN_SCHEMA = { type: 'boolean' } satisfies JSONSchema7;
const NUMBER_SCHEMA = { type: 'number' } satisfies JSONSchema7;

const PHONE_NUMBER_SCHEMA = {
  type: 'object',
  properties: { phoneNumberToHold: STRING_SCHEMA },
  additionalProperties: false,
} satisfies JSONSchema7;

const SET_MODE_SCHEMA = {
  type: 'object',
  required: ['mode', 'status', 'reconnect'],
  properties: {
    mode: {
      type: 'string',
      enum: [...OZONE_TEL_AGENT_MODES],
    },
    status: { type: 'string', enum: [...OZONE_TEL_AGENT_STATUSES] },
    reconnect: BOOLEAN_SCHEMA,
  },
  additionalProperties: false,
} satisfies JSONSchema7;

const START_CALL_SCHEMA = {
  type: 'object',
  required: ['campaignId', 'phoneNumber'],
  properties: {
    campaignId: STRING_SCHEMA,
    phoneNumber: NUMBER_SCHEMA,
    redial: BOOLEAN_SCHEMA,
  },
  additionalProperties: false,
} satisfies JSONSchema7;

export const OZT_METHOD_PAGE_EVENTS = [
  {
    eventType: OZT_METHOD_TYPES.initialize,
    label: 'Initialize',
    description:
      'Authenticate and log a human agent into the Ozonetel dialer. Must be invoked once on page load before any other call method, otherwise subsequent actions will fail.',
    payload: initializeSchema,
  },
  {
    eventType: OZT_METHOD_TYPES.startCall,
    label: 'Start Call',
    description:
      'Place an outbound manual call on the specified campaign to the given phone number. Optionally redial if the number was previously attempted on the same campaign.',
    payload: START_CALL_SCHEMA,
  },
  {
    eventType: OZT_METHOD_TYPES.endCall,
    label: 'End Call',
    description:
      'Hang up the currently active call from the agent side, terminating the connection for both parties.',
  },
  {
    eventType: OZT_METHOD_TYPES.hold,
    label: 'Hold Phone Number',
    description:
      'Place the specified caller on hold, muting both sides and typically playing hold music until the caller is resumed.',
    payload: PHONE_NUMBER_SCHEMA,
  },
  {
    eventType: OZT_METHOD_TYPES.unhold,
    label: 'Unhold Phone Number',
    description:
      'Resume a previously held caller, restoring two-way audio between the agent and the caller.',
    payload: PHONE_NUMBER_SCHEMA,
  },
  {
    eventType: OZT_METHOD_TYPES.mute,
    label: 'Mute Call (contact)',
    description:
      'Mute the contact on the active call. The agent can continue speaking, but contact audio is muted by the Ozonetel SDK.',
  },
  {
    eventType: OZT_METHOD_TYPES.unmute,
    label: 'Unmute Call (contact)',
    description:
      'Unmute the contact on the active call, restoring contact audio through the Ozonetel SDK.',
  },
  {
    eventType: OZT_METHOD_TYPES.muteAgent,
    label: 'Mute Call (agent)',
    description:
      "Mute the browser agent's microphone on the active call while keeping the Ozonetel SDK audio stream connected.",
  },
  {
    eventType: OZT_METHOD_TYPES.unmuteAgent,
    label: 'Unmute Call (agent)',
    description:
      "Unmute the browser agent's microphone on the active call, restoring outbound agent audio to the contact.",
  },
  {
    eventType: OZT_METHOD_TYPES.setMode,
    label: 'Set Mode',
    description:
      'Switch the agent between dialer modes (INBOUND, MANUAL, PROGRESSIVE…) and availability status (READY, BUSY, IDLE). Use to change how calls are routed or to mark the agent available between sessions.',
    payload: SET_MODE_SCHEMA,
  },
  {
    eventType: OZT_METHOD_TYPES.logout,
    label: 'Logout',
    description:
      'End the agent session and sign out of Ozonetel. After logout the agent will no longer receive or be able to place calls until re-initialized.',
  },
  {
    eventType: OZT_METHOD_TYPES.getCallData,
    label: 'Get Call Data',
    description:
      'Fetch metadata for the currently active call such as caller ID, call UUID, campaign, and direction. Useful for attaching call context to CRM records.',
  },
  {
    eventType: OZT_METHOD_TYPES.releaseAgent,
    label: 'Release Agent',
    description:
      'Release the agent back to the ready pool in MANUAL mode between calls, signaling the dialer that the agent is free to take or place the next call.',
  },
  {
    eventType: OZT_METHOD_TYPES.getCallRecording,
    label: 'Get Call Recording',
    description:
      "Fetch the recording URL of the last completed call from Ozonetel's history. Response is delivered asynchronously via the On Call Recording callback event.",
  },
] satisfies PageEventType[];

export const OZT_CALLBACK_PAGE_EVENTS = [
  {
    eventType: 'ozt_onSipRegistered',
    label: 'On Agent Ready',
    description:
      'Fires once the softphone has registered and the agent can actually place calls. This lands a moment after Initialize succeeds — a call started before it is discarded by Ozonetel without an error, so use this to enable the call controls.',
  },
  {
    eventType: 'ozt_onSipDisconnected',
    label: 'On Agent Not Ready',
    description:
      'Fires when the softphone drops or fails to register, including after a long spell in a background browser tab. Use this to disable the call controls until On Agent Ready fires again.',
  },
  {
    eventType: 'ozt_onDropCall',
    label: 'On Drop Call',
    description:
      'Fires when either party disconnects and the call ends while ringing. Use to reset UI state, stop timers, or trigger post-call workflows.',
  },
  {
    eventType: 'ozt_onAgentRelease',
    label: 'On Agent Release',
    description:
      'Fires when either party disconnects and the call ends after it was connected. Can be used with "On Drop Call" to handle all the cases where a call can get dropped.',
  },
  {
    eventType: 'ozt_onAgentCallAnswered',
    label: 'On Agent Call Answered',
    description:
      'Fires when the agent has picked up and the call is live with two-way audio established. Use to start the call timer and enable in-call controls.',
  },
  {
    eventType: 'ozt_onNewCall',
    label: 'On New Call',
    description:
      'Fires when an inbound call is offered to the agent, before it has been answered. Use to surface the incoming call UI with caller details.',
  },
  {
    eventType: 'ozt_onTranscribeText',
    label: 'On Transcribe Text',
    description:
      'Fires when a real-time transcription chunk arrives during the call. Only emitted if live transcription is enabled on your Ozonetel tenant.',
  },
  {
    eventType: 'ozt_onCallDetails',
    label: 'On Call Details',
    description:
      'Fires when Ozonetel pushes an updated call details payload, including caller metadata, campaign info, and custom variables attached to the call.',
  },
  {
    eventType: 'ozt_onCallRecording',
    label: 'On Call Recording',
    description:
      'Fires in response to Get Call Recording once the recording URL is ready. Payload contains the playable recording link for the last completed call.',
  },
] satisfies PageEventType[];
